import { Controller } from "@hotwired/stimulus";

// Handles online payment form, fee preview, and payment status polling
export default class extends Controller {
  static values = {
    schoolId: String,
    amount: Number,
    feePreviewUrl: String,
    paymentStatusUrl: String,
    paymentId: String,
    polling: { type: Boolean, default: false },
    refreshInterval: { type: Number, default: 5000 },
  };

  static targets = [
    "form",
    "phoneInput",
    "feeBreakdown",
    "submitBtn",
    "submitText",
    "spinner",
    "statusContainer",
    "statusMessage",
    "statusTitle",
  ];

  connect() {
    if (this.pollingValue && this.paymentIdValue) {
      this.startPolling();
    }

    // Pick up any pre-selected operator on connect
    const checked = this.element.querySelector(
      'input[name="payment_method"]:checked',
    );
    if (checked) {
      this.selectedOperator = checked.value;
      this.loadFeePreview();
      this.updateSubmitState();
    }
  }

  disconnect() {
    this.stopPolling();
    if (this.redirectCountdown) {
      clearInterval(this.redirectCountdown);
    }
  }

  selectOperator(event) {
    const radio =
      event.target.closest("label")?.querySelector('input[type="radio"]') ||
      event.target;
    this.selectedOperator = radio.value;
    this.updateSubmitState();
    this.loadFeePreview();
  }

  onPhoneInput() {
    this.updateSubmitState();
  }

  updateSubmitState() {
    if (!this.hasSubmitBtnTarget) return;

    const hasOperator = !!this.selectedOperator;
    const hasPhone =
      this.hasPhoneInputTarget && this.phoneInputTarget.value.length >= 9;
    this.submitBtnTarget.disabled = !(hasOperator && hasPhone);
  }

  async loadFeePreview() {
    if (!this.selectedOperator || !this.feePreviewUrlValue) return;

    try {
      const params = new URLSearchParams({
        school_id: this.schoolIdValue,
        amount: this.amountValue,
        operator: this.selectedOperator,
        payment_type: "online",
      });

      const response = await fetch(`${this.feePreviewUrlValue}?${params}`, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token":
            document.querySelector('meta[name="csrf-token"]')?.content || "",
        },
        credentials: "same-origin",
      });

      if (response.ok) {
        const data = await response.json();
        this.renderFeeBreakdown(data);
      } else {
        console.error(
          "Fee preview failed:",
          response.status,
          response.statusText,
        );
      }
    } catch (error) {
      console.error("Error loading fee preview:", error);
    }
  }

  renderFeeBreakdown(data) {
    if (!this.hasFeeBreakdownTarget) return;

    const formatCurrency = (amount) => {
      return Math.round(amount).toLocaleString("fr-FR") + " FCFA";
    };

    const d = data.display || {};

    let html = '<div class="bg-gray-50 rounded-md p-4 mb-4 space-y-2">';
    html +=
      '<p class="text-sm font-medium text-gray-700 mb-2">Frais de transaction</p>';
    html += `<div class="flex justify-between text-sm"><span class="text-gray-500">Montant du panier</span><span class="text-gray-900">${formatCurrency(d.cart_amount)}</span></div>`;

    if (d.service_fees > 0) {
      html += `<div class="flex justify-between text-sm"><span class="text-gray-500">Frais de service</span><span class="text-gray-900">${formatCurrency(d.service_fees)}</span></div>`;
    }

    if (d.withdrawal_fees > 0) {
      html += `<div class="flex justify-between text-sm"><span class="text-gray-500">Frais de retrait</span><span class="text-gray-900">${formatCurrency(d.withdrawal_fees)}</span></div>`;
    }

    html +=
      '<div class="flex justify-between text-sm font-bold border-t border-gray-200 pt-2 mt-2">';
    html += `<span class="text-gray-900">Total à payer</span><span class="text-cta">${formatCurrency(d.total)}</span>`;
    html += "</div></div>";

    this.feeBreakdownTarget.innerHTML = html;
  }

  async submit(event) {
    event.preventDefault();
    if (!this.hasFormTarget) return;

    this.showProcessing();

    try {
      const formData = new FormData(this.formTarget);
      const response = await fetch(this.formTarget.action, {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            .content,
        },
        credentials: "same-origin",
      });

      const data = await response.json();

      if (data.success) {
        this.paymentIdValue = data.primary_payment?.id || data.payment_id;
        this.startPolling();
      } else {
        this.showError(data.error || "Une erreur est survenue");
      }
    } catch (error) {
      console.error("Payment error:", error);
      this.showError("Une erreur est survenue lors du paiement");
    }
  }

  showProcessing() {
    if (this.hasFormTarget) this.formTarget.classList.add("hidden");
    if (this.hasStatusContainerTarget)
      this.statusContainerTarget.classList.remove("hidden");
    if (this.hasSubmitTextTarget) this.submitTextTarget.classList.add("hidden");
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.remove("hidden");
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = true;
  }

  showError(message) {
    if (this.hasFormTarget) this.formTarget.classList.remove("hidden");
    if (this.hasStatusContainerTarget)
      this.statusContainerTarget.classList.add("hidden");
    if (this.hasSubmitTextTarget)
      this.submitTextTarget.classList.remove("hidden");
    if (this.hasSpinnerTarget) this.spinnerTarget.classList.add("hidden");
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = false;

    if (window.Toast) {
      window.Toast.show(message, "error");
    } else {
      alert(message);
    }
  }

  startPolling() {
    this.stopPolling();
    this.pollInterval = setInterval(() => {
      this.checkPaymentStatus();
    }, this.refreshIntervalValue);
  }

  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  async checkPaymentStatus() {
    if (!this.paymentIdValue || !this.paymentStatusUrlValue) return;

    try {
      const params = new URLSearchParams({ payment_id: this.paymentIdValue });
      const response = await fetch(`${this.paymentStatusUrlValue}?${params}`, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      });

      if (!response.ok) return;

      const data = await response.json();

      if (data.status === "completed") {
        this.stopPolling();
        this.showSuccess(data);
      } else if (data.status === "failed") {
        this.stopPolling();
        this.showFailure(data);
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
    }
  }

  showSuccess(data) {
    if (this.hasStatusTitleTarget) {
      this.statusTitleTarget.textContent = "Paiement réussi !";
    }
    if (this.hasStatusMessageTarget) {
      const formatted = Math.round(data.amount).toLocaleString("fr-FR");
      this.statusMessageTarget.textContent = `Votre paiement de ${formatted} FCFA a été confirmé.`;
    }
    if (this.hasStatusContainerTarget) {
      const spinner = this.statusContainerTarget.querySelector(".animate-spin");
      if (spinner) {
        spinner.outerHTML =
          '<div class="w-16 h-16 rounded-full bg-cta-shared flex items-center justify-center mx-auto mb-4"><svg class="text-cta" width="32" height="32" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></div>';
      }
    }

    // Auto-redirect after 3 seconds
    if (data.redirect_url) {
      let seconds = 3;
      const redirectMsg = document.createElement("p");
      redirectMsg.className = "text-sm text-gray-500 mt-3";
      redirectMsg.textContent = `Redirection dans ${seconds} secondes...`;
      if (this.hasStatusMessageTarget) {
        this.statusMessageTarget.parentElement.appendChild(redirectMsg);
      }

      this.redirectCountdown = setInterval(() => {
        seconds -= 1;
        if (seconds <= 0) {
          clearInterval(this.redirectCountdown);
          window.location.href = data.redirect_url;
        } else {
          redirectMsg.textContent = `Redirection dans ${seconds} secondes...`;
        }
      }, 1000);
    }
  }

  showFailure(data) {
    if (this.hasStatusTitleTarget) {
      this.statusTitleTarget.textContent = "Paiement échoué";
    }
    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.textContent =
        "Le paiement n'a pas pu être traité. Veuillez réessayer.";
    }
    if (this.hasStatusContainerTarget) {
      const spinner = this.statusContainerTarget.querySelector(".animate-spin");
      if (spinner) {
        spinner.outerHTML =
          '<div class="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4"><svg width="32" height="32" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6L18 18" stroke="#FF3235" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></div>';
      }

      // Add back-to-checkout link
      const backLink = document.createElement("div");
      backLink.className = "flex justify-center gap-3 mt-4";
      backLink.innerHTML = `<a href="${data.redirect_url || window.location.pathname.replace("/payment_status", "/checkout")}" class="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-md transition-colors">Retour</a>`;
      this.statusContainerTarget.appendChild(backLink);
    }
  }
}
